// #tag: STUDENT DASHBOARD SCREEN
// #purpose: Personalized student command center displaying saved exam alerts, milestone countdowns, unread notifications, and quick vault access.
import React from 'react';
import { Bell, Calendar, ClipboardList, Heart, Trophy } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ResultCard, StatCard, TimelineView } from '../components/common/Cards';
import { PageHeaderSection } from '../components/cms/PageSections';
import { useSavedExams } from '../context/AuthProvider';
import {
  useCalendarEvents,
  useDashboardSummary,
  useDashboardWidgets,
  useExamTimeline,
  useExamTimelineByExamId,
  useLatestResults,
  useNotifications,
  usePageSections,
} from '../hooks/useExamData';
import type { DashboardSummaryDto, DashboardWidgetDto } from '../api/cms';
import { ErrorState, LoadingState } from '../components/states/StateViews';
import { Illustration } from '../components/common/Illustration';
import { resolveIcon } from '../lib/iconMap';
import { calendarEventRoute, formatCalendarEventType } from '../lib/calendarEventUi';
import type { LucideIcon } from 'lucide-react';
import type { SavedExam } from '../types';

function configString(config: Record<string, unknown>, key: string, fallback = ''): string {
  const value = config[key];
  return typeof value === 'string' && value.trim() ? value : fallback;
}

function configNumber(config: Record<string, unknown>, key: string, fallback: number): number {
  const value = config[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function configBool(config: Record<string, unknown>, key: string): boolean {
  return config[key] === true;
}

function WidgetHead({
  iconName,
  title,
  actionLabel,
  onAction,
  fallbackIcon,
}: {
  iconName?: string;
  title: string | null;
  actionLabel?: string;
  onAction?: () => void;
  fallbackIcon: LucideIcon;
}) {
  const Icon = resolveIcon(iconName, fallbackIcon);
  return (
    <div className="wfx-dash-card-head">
      <Icon size={20} aria-hidden="true" />
      <h2>{title}</h2>
      {actionLabel && onAction && (
        <button type="button" className="text-action" onClick={onAction}>{actionLabel}</button>
      )}
    </div>
  );
}

function DashCard({
  illustration,
  emptyIllustration,
  isEmpty = false,
  className = '',
  children,
}: {
  illustration?: string;
  emptyIllustration?: string;
  isEmpty?: boolean;
  className?: string;
  children: React.ReactNode;
}) {
  const emptyContext = emptyIllustration ?? illustration;
  const showWatermark = Boolean(illustration) && !isEmpty;
  const showEmptyArt = isEmpty && Boolean(emptyContext);

  return (
    <article
      className={`dash-card glass-surface${showWatermark || showEmptyArt ? ' has-illustration' : ''}${className ? ` ${className}` : ''}`}
    >
      {showWatermark && (
        <Illustration context={illustration!} size="xs" className="wfx-dash-card-illustration" />
      )}
      <div className="wfx-dash-card-body">
        {showEmptyArt && (
          <Illustration context={emptyContext!} size="sm" className="wfx-dash-card-empty-illustration" />
        )}
        {children}
      </div>
    </article>
  );
}

function ExamTimelineWidget({
  widget,
  savedExams,
}: {
  widget: DashboardWidgetDto;
  savedExams: SavedExam[];
}) {
  const config = widget.configuration as Record<string, unknown>;
  const wide = config.wide === true;
  const timelineSource = configString(config, 'timelineSource', 'first_saved_exam');
  const titleSuffix = configString(config, 'titleSuffix');
  const layout = configString(config, 'layout', 'horizontal') === 'vertical' ? 'vertical' : 'horizontal';
  const primarySavedExamId = savedExams[0]?.id;

  const useSavedExamTimeline = timelineSource === 'first_saved_exam';
  const fixedTimelineId = timelineSource === 'fixed' ? configString(config, 'timelineId') : '';

  const { data: timelineByExam, isFetched: timelineByExamFetched } = useExamTimelineByExamId(
    useSavedExamTimeline && primarySavedExamId ? primarySavedExamId : undefined,
  );
  const { data: timelineById } = useExamTimeline(
    fixedTimelineId || undefined,
  );

  let timeline = useSavedExamTimeline ? timelineByExam : timelineById;
  if (useSavedExamTimeline && !primarySavedExamId) {
    timeline = undefined;
  }

  let emptyMessage = configString(config, 'emptyMessage');
  if (useSavedExamTimeline && primarySavedExamId && timelineByExamFetched && !timelineByExam) {
    emptyMessage = configString(config, 'noTimelineMessage', emptyMessage);
  }

  if (!timeline) {
    return (
      <DashCard
        illustration={configString(config, 'illustration')}
        emptyIllustration={configString(config, 'emptyIllustration')}
        isEmpty
        className={wide ? 'wfx-dash-wide' : ''}
      >
        <WidgetHead
          iconName={configString(config, 'icon')}
          title={widget.title}
          fallbackIcon={ClipboardList}
        />
        <p className="wfx-muted">{emptyMessage}</p>
      </DashCard>
    );
  }

  return (
    <DashCard
      illustration={configString(config, 'illustration')}
      className={wide ? 'wfx-dash-wide' : ''}
    >
      <WidgetHead
        iconName={configString(config, 'icon')}
        title={`${timeline.examTitle}${titleSuffix}`}
        fallbackIcon={ClipboardList}
      />
      <TimelineView
        steps={timeline.steps as Parameters<typeof TimelineView>[0]['steps']}
        layout={layout}
      />
    </DashCard>
  );
}

function WidgetRenderer({
  widget,
  summary,
  savedExams,
}: {
  widget: DashboardWidgetDto;
  summary: DashboardSummaryDto | undefined;
  savedExams: SavedExam[];
}) {
  const navigate = useNavigate();
  const config = widget.configuration as Record<string, unknown>;
  const wide = config.wide === true;
  const limit = configNumber(config, 'limit', 5);
  const filterBySavedExams = configBool(config, 'filterBySavedExams');
  const examIds = filterBySavedExams ? savedExams.map((exam) => exam.id) : undefined;

  const { data: calendarData } = useCalendarEvents({
    limit,
    examIds,
    upcoming: configBool(config, 'upcomingOnly') || filterBySavedExams,
  });
  const { data: latestData, isLoading: resultsLoading } = useLatestResults(
    configNumber(config, 'limit', 2),
  );
  const { data: notifData } = useNotifications({
    limit: configNumber(config, 'limit', 3),
  });

  switch (widget.widgetType) {
    case 'stats_row': {
      const stats = (config.stats as Array<{ label: string; source: string; tone: string; illustration?: string }>) ?? [];
      const values: Record<string, number> = {
        saved_exams_count: summary?.savedExamsCount ?? 0,
        unread_notifications_count: summary?.unreadNotificationsCount ?? 0,
        upcoming_events_count: summary?.upcomingEventsCount ?? 0,
        vault_documents_count: summary?.vaultDocumentsCount ?? 0,
      };
      return (
        <div className="wfx-dash-stats wfx-dash-wide">
          {stats.map((stat) => (
            <StatCard
              key={stat.label}
              label={stat.label}
              value={values[stat.source] ?? 0}
              tone={stat.tone}
              illustration={stat.illustration}
            />
          ))}
        </div>
      );
    }
    case 'saved_exams':
      return (
        <DashCard
          illustration={configString(config, 'illustration')}
          emptyIllustration={configString(config, 'emptyIllustration')}
          isEmpty={savedExams.length === 0}
          className={wide ? 'wfx-dash-wide' : ''}
        >
          <WidgetHead
            iconName={configString(config, 'icon')}
            title={widget.title}
            actionLabel={configString(config, 'browseLabel')}
            onAction={
              typeof config.browseRoute === 'string'
                ? () => navigate(config.browseRoute as string)
                : undefined
            }
            fallbackIcon={Heart}
          />
          {savedExams.length === 0 ? (
            <p className="wfx-muted wfx-dash-empty-copy">{configString(config, 'emptyMessage')}</p>
          ) : (
            <ul className="wfx-saved-list">
              {savedExams.map((exam) => (
                <li key={exam.id}>
                  <span className="chip-pill">{exam.examCategory}</span>
                  <strong>{exam.title}</strong>
                  <span className="wfx-muted">{exam.organization}</span>
                </li>
              ))}
            </ul>
          )}
        </DashCard>
      );
    case 'upcoming_events': {
      const events = calendarData?.data ?? [];
      const typeRoutes = config.typeRoutes as Record<string, string> | undefined;
      return (
        <DashCard
          illustration={configString(config, 'illustration')}
          emptyIllustration={configString(config, 'emptyIllustration')}
          isEmpty={events.length === 0}
        >
          <WidgetHead
            iconName={configString(config, 'icon')}
            title={widget.title}
            actionLabel={configString(config, 'viewAllLabel')}
            onAction={
              typeof config.viewAllRoute === 'string'
                ? () => navigate(config.viewAllRoute as string)
                : undefined
            }
            fallbackIcon={Calendar}
          />
          {events.length === 0 ? (
            <p className="wfx-muted wfx-dash-empty-copy">{configString(config, 'emptyMessage')}</p>
          ) : (
            <ul className="wfx-event-list">
              {events.map((event) => (
                <li key={event.id}>
                  <button
                    type="button"
                    className="wfx-event-row"
                    onClick={() => navigate(calendarEventRoute(event, typeRoutes))}
                  >
                    <span className="wfx-event-date">{event.date}</span>
                    <span className="wfx-event-title">{event.title}</span>
                    <span className="chip-pill wfx-event-type">{formatCalendarEventType(event.type)}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </DashCard>
      );
    }
    case 'exam_timeline':
      return <ExamTimelineWidget widget={widget} savedExams={savedExams} />;
    case 'latest_results': {
      const results = latestData?.data ?? [];
      return (
        <DashCard
          illustration={configString(config, 'illustration')}
          emptyIllustration={configString(config, 'emptyIllustration')}
          isEmpty={!resultsLoading && results.length === 0}
        >
          <WidgetHead
            iconName={configString(config, 'icon')}
            title={widget.title}
            actionLabel={configString(config, 'viewAllLabel')}
            onAction={
              typeof config.viewAllRoute === 'string'
                ? () => navigate(config.viewAllRoute as string)
                : undefined
            }
            fallbackIcon={Trophy}
          />
          <div className="wfx-dash-results">
            {resultsLoading ? (
              <LoadingState label={configString(config, 'loadingLabel', 'Loading…')} />
            ) : results.length === 0 ? (
              <p className="wfx-muted wfx-dash-empty-copy">{configString(config, 'emptyMessage')}</p>
            ) : (
              results.map((result) => <ResultCard key={result.id} result={result} />)
            )}
          </div>
        </DashCard>
      );
    }
    case 'recent_notifications': {
      const notifications = (notifData?.data ?? []).slice(0, limit);
      return (
        <DashCard
          illustration={configString(config, 'illustration')}
          emptyIllustration={configString(config, 'emptyIllustration')}
          isEmpty={notifications.length === 0}
        >
          <WidgetHead
            iconName={configString(config, 'icon')}
            title={widget.title}
            actionLabel={configString(config, 'viewAllLabel')}
            onAction={
              typeof config.viewAllRoute === 'string'
                ? () => navigate(config.viewAllRoute as string)
                : undefined
            }
            fallbackIcon={Bell}
          />
          {notifications.length === 0 ? (
            <p className="wfx-muted wfx-dash-empty-copy">{configString(config, 'emptyMessage')}</p>
          ) : (
            <ul className="wfx-notif-list compact">
              {notifications.map((notification) => (
                <li key={notification.id} className={notification.read ? '' : 'is-unread'}>
                  <strong>{notification.title}</strong>
                  <span className="wfx-muted">{notification.message}</span>
                </li>
              ))}
            </ul>
          )}
        </DashCard>
      );
    }
    default:
      return null;
  }
}

export function DashboardScreen() {
  const { savedExams } = useSavedExams();
  const { data: sectionsData } = usePageSections('dashboard');
  const headerSection = sectionsData?.data?.[0];
  const {
    data: widgetsData,
    isLoading: widgetsLoading,
    isError: widgetsError,
    refetch: refetchWidgets,
  } = useDashboardWidgets();
  const { data: summary } = useDashboardSummary();

  const pageConfig = (headerSection?.configuration ?? {}) as Record<string, unknown>;
  const loadingLabel = configString(pageConfig, 'loadingLabel', 'Loading dashboard…');

  if (widgetsLoading) {
    return (
      <section className="adaptive-page">
        <LoadingState label={loadingLabel} />
      </section>
    );
  }

  if (widgetsError) {
    return (
      <section className="adaptive-page">
        <ErrorState message="Could not load dashboard." onRetry={() => void refetchWidgets()} />
      </section>
    );
  }

  const widgets = widgetsData?.data ?? [];

  return (
    <section className="adaptive-page wfx-dashboard">
      {headerSection && <PageHeaderSection section={headerSection} />}

      <div className="wfx-dash-grid">
        {widgets.map((widget) => (
          <WidgetRenderer
            key={widget.id}
            widget={widget}
            summary={summary}
            savedExams={savedExams}
          />
        ))}
      </div>
    </section>
  );
}
